import '@atlaskit/css-reset';
import HeartbeatDisplay from '@atlassian/heartbeat';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { RelayEnvironmentProvider } from 'react-relay';
import { matchRoute, Router, RouterContext, createBrowserHistory, useQueryParam } from 'react-resource-router';

import { AnalyticsClientImpl, AnalyticsContext, useAtlasKitAnalytics } from '@townsquare/analytics';
import { AkAnalyticsListeners } from '@townsquare/analytics/ak-listener';
import { returnAfterLogin } from '@townsquare/auth-utils';
import { CrossFlowProvider, CrossFlowWAC } from '@townsquare/crossflow';
import { Drawers } from '@townsquare/drawers';
import { FullPageError } from '@townsquare/error-state';
import { Flags } from '@townsquare/flags';
import { TownsquareIntlProvider, UserLocaleOverrideHandler } from '@townsquare/i18n';
import { Intercom } from '@townsquare/intercom';
import { installSentry } from '@townsquare/logging';
import { Modals } from '@townsquare/modals/ui';
import { HelpContextProvider } from '@townsquare/navigation';
import { RouteListener } from '@townsquare/performance';
import PostOfficeContextProvider from '@townsquare/post-office';
import { RelayEnvironment } from '@townsquare/relay-utils';
import { isEmbedRoute } from '@townsquare/router/primitives';
import { useRouteListeners } from '@townsquare/router/use-route-listeners';
import { useIsNavRefreshEnabled } from '@townsquare/stat-sig/nav4';
import { GlobalTheming } from '@townsquare/theme';
import { createSearchParamsFromCurrentUrl } from '@townsquare/url-utils/search-params';

import { AnalyticAttributeHandler } from './AnalyticsAttributeHandler';
import { AuthCheck } from './auth/AuthCheck';
import { StorageAuthCheck } from './auth/StorageAuthCheck';
import { Bootstrap } from './bootstrap/Bootstrap';
import { StatSigInit } from './bootstrap/StatSigInit';
import { preloadRouteComponent } from './bootstrap/preloadRouteComponent';
import { EmbedRoute } from './router/EmbedRoute';
import { GriffinRoute } from './router/GriffinRoute';
import { Route } from './router/Route';
import { useRoute, useHomeRoutes } from './router/hooks';
import { routes } from './router/routes';

const analyticsClient = new AnalyticsClientImpl('web', 'townsquare');
const history = createBrowserHistory();
installSentry();

const EnforceCloudId = (props: { cloudId?: string }) => {
  const route = useRoute();
  const [qsCloudId, setQsCloudId] = useQueryParam('cloudId');

  if ('routeType' in route && route.routeType === 'global' && qsCloudId !== props.cloudId) {
    setQsCloudId(props.cloudId, 'replace');
  }

  return null;
};

export function App() {
  const onPrefetch = (routerContext: RouterContext) => preloadRouteComponent(routerContext.route);
  const homeRoutes = useHomeRoutes();

  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
  const AuthenticationCheck = isEmbedRoute(matchedRoute?.route) ? StorageAuthCheck : AuthCheck;

  return (
    <AuthenticationCheck>
      <TownsquareIntlProvider>
        <RelayEnvironmentProvider environment={RelayEnvironment}>
          <AnalyticsContext.Provider value={analyticsClient}>
            <PostOfficeContextProvider>
              <CrossFlowProvider>
                <HelpContextProvider>
                  <Bootstrap history={history}>
                    {data => (
                      <ErrorBoundary fallback={<FullPageError />}>
                        <EnforceCloudId cloudId={data.workspace?.cloudId} />
                        <AnalyticAttributeHandler />
                        <UserLocaleOverrideHandler />
                        <StatSigInit
                          accountId={data.user.accountId}
                          orgId={data.orgId}
                          cloudId={data.workspace?.cloudId}
                        >
                          <GlobalTheming />
                          <HeartbeatDisplay onAuthenticationFailed={returnAfterLogin} />
                          <Router routes={homeRoutes} history={history} resourceContext={data} onPrefetch={onPrefetch}>
                            <RouteListener />
                            <RouteSwitcher />
                          </Router>
                          <Intercom />
                          <Modals />
                          <Drawers />
                          <Flags />
                        </StatSigInit>
                      </ErrorBoundary>
                    )}
                  </Bootstrap>
                </HelpContextProvider>
              </CrossFlowProvider>
            </PostOfficeContextProvider>
            {/* WAC via Start Cross Flow touch point support */}
            <CrossFlowWAC />
          </AnalyticsContext.Provider>
        </RelayEnvironmentProvider>
      </TownsquareIntlProvider>
    </AuthenticationCheck>
  );
}

function RouteSwitcher() {
  const { embed } = useRoute();
  const akAnalytics = useAtlasKitAnalytics();
  const isNavRefreshEnabled = useIsNavRefreshEnabled();
  useRouteListeners();

  return (
    <AkAnalyticsListeners client={akAnalytics}>
      {embed ? <EmbedRoute /> : isNavRefreshEnabled ? <GriffinRoute /> : <Route />}
    </AkAnalyticsListeners>
  );
}
